/**
 * supportTicketsService — accesso dati della coda ticket di assistenza
 * lato Operator Console (super utente).
 *
 * Pattern console: letture/scritture Firestore DIRETTE (la console è solo
 * super_admin, le security rules bloccano chiunque altro), come
 * `platformCrmService`. Le scritture che dipendono dallo stato corrente del
 * ticket (prima risposta, prima risoluzione, contatore messaggi) usano una
 * TRANSAZIONE: il check "se null" va fatto sul dato fresco, non su quello
 * caricato in lista (potrebbe essere stantìo).
 */

import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import type {
  SupportTicket,
  SupportTicketMessage,
  SupportTicketPriorita,
  SupportTicketStato,
} from '../types/supportTickets';

const TICKETS_COLLECTION = 'support_tickets';
const MESSAGES_SUBCOLLECTION = 'messages';

/** Nome pubblico con cui firma ogni risposta dell'operatore. */
export const OPERATOR_AUTHOR_NAME = 'Assistenza YumaOS';

/** Limite testo messaggio (allineato al modello dati `messages.testo`). */
export const MAX_MESSAGE_LENGTH = 5000;

// ============================================================================
// MAPPING — regola progetto: mai whitelist, sempre spread + override id
// ============================================================================

function mapSnap<T>(d: QueryDocumentSnapshot<DocumentData>): T {
  return { ...(d.data() as Record<string, unknown>), id: d.id } as T;
}

// ============================================================================
// LETTURE
// ============================================================================

/**
 * Coda ticket CROSS-TENANT, ordinata per ultimo messaggio (i thread con
 * attività recente in cima). Query globale su un solo campo: nessun indice
 * composito richiesto.
 */
export async function listSupportTickets(): Promise<SupportTicket[]> {
  const q = query(
    collection(db, TICKETS_COLLECTION),
    orderBy('last_message_at', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapSnap<SupportTicket>(d));
}

/** Thread messaggi di un ticket in ordine cronologico. */
export async function listTicketMessages(ticketId: string): Promise<SupportTicketMessage[]> {
  const q = query(
    collection(db, TICKETS_COLLECTION, ticketId, MESSAGES_SUBCOLLECTION),
    orderBy('created_at', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapSnap<SupportTicketMessage>(d));
}

// ============================================================================
// SCRITTURE
// ============================================================================

/**
 * Invia una risposta dell'operatore sul thread del ticket.
 *
 * In un'unica TRANSAZIONE:
 * - crea il messaggio (`autore_tipo='operatore'`, firma "Assistenza YumaOS");
 * - aggiorna il ticket: `last_message_at`/`last_message_by`, incremento
 *   `messaggi_count`, `updated_at`;
 * - se è la PRIMA risposta di un operatore (`first_response_at` ancora null)
 *   scrive `first_response_at = serverTimestamp()`. Il check è sul dato
 *   fresco letto in transazione, così due invii ravvicinati non possono
 *   sovrascrivere la metrica di prima risposta.
 */
export async function sendOperatorMessage(ticketId: string, testo: string): Promise<void> {
  const trimmed = testo.trim();
  if (!trimmed) {
    throw new Error('il testo della risposta è vuoto');
  }
  if (trimmed.length > MAX_MESSAGE_LENGTH) {
    throw new Error(`la risposta supera il limite di ${MAX_MESSAGE_LENGTH} caratteri`);
  }
  const uid = auth.currentUser?.uid;
  if (!uid) {
    throw new Error('operatore non autenticato');
  }

  const ticketRef = doc(db, TICKETS_COLLECTION, ticketId);
  const messageRef = doc(collection(db, TICKETS_COLLECTION, ticketId, MESSAGES_SUBCOLLECTION));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ticketRef);
    if (!snap.exists()) {
      throw new Error('ticket non trovato');
    }
    const data = snap.data() as Partial<SupportTicket>;

    tx.set(messageRef, {
      autore_uid: uid,
      autore_nome: OPERATOR_AUTHOR_NAME,
      autore_tipo: 'operatore',
      testo: trimmed,
      allegati: [],
      created_at: serverTimestamp(),
    });

    const patch: Record<string, unknown> = {
      last_message_at: serverTimestamp(),
      last_message_by: 'operatore',
      messaggi_count: (typeof data.messaggi_count === 'number' ? data.messaggi_count : 0) + 1,
      updated_at: serverTimestamp(),
    };
    if (data.first_response_at == null) {
      patch.first_response_at = serverTimestamp();
    }
    tx.update(ticketRef, patch);
  });
}

/**
 * Cambia lo stato del ticket (transazione: il check "se null" sui timestamp
 * va fatto sul documento fresco).
 *
 * - `risolto` → scrive anche `resolved_at = serverTimestamp()` SE ancora null;
 * - `chiuso`  → scrive anche `closed_at = serverTimestamp()` SE ancora null.
 *
 * RIAPERTURA (scelta documentata): tornare da risolto/chiuso a uno stato di
 * lavorazione NON pulisce `resolved_at`/`closed_at`. Restano come marcatori
 * storici della PRIMA risoluzione/chiusura; lo stato corrente autoritativo è
 * `stato`. Un ticket con stato attivo e `resolved_at` valorizzato risulta
 * quindi RIAPERTO — la metrica di risoluzione continua a misurare la prima
 * risoluzione, senza riscriverla a ogni ciclo.
 */
export async function updateTicketStato(
  ticketId: string,
  stato: SupportTicketStato,
): Promise<void> {
  const ticketRef = doc(db, TICKETS_COLLECTION, ticketId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ticketRef);
    if (!snap.exists()) {
      throw new Error('ticket non trovato');
    }
    const data = snap.data() as Partial<SupportTicket>;
    const patch: Record<string, unknown> = {
      stato,
      updated_at: serverTimestamp(),
    };
    if (stato === 'risolto' && data.resolved_at == null) {
      patch.resolved_at = serverTimestamp();
    }
    if (stato === 'chiuso' && data.closed_at == null) {
      patch.closed_at = serverTimestamp();
    }
    tx.update(ticketRef, patch);
  });
}

/** Cambia la priorità del ticket (updateDoc diretto, pattern console). */
export async function updateTicketPriorita(
  ticketId: string,
  priorita: SupportTicketPriorita,
): Promise<void> {
  await updateDoc(doc(db, TICKETS_COLLECTION, ticketId), {
    priorita,
    updated_at: serverTimestamp(),
  });
}

// ============================================================================
// ALLEGATI
// ============================================================================

/**
 * URL di download di un allegato dal suo path Storage
 * (`tenants/{tenant_id}/support_tickets/{ticket_id}/{filename}`).
 * Errori propagati al chiamante: niente fallback silenziosi.
 */
export async function getAllegatoDownloadUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}

/** Nome file leggibile da un path Storage (ultimo segmento). */
export function allegatoFileName(storagePath: string): string {
  const segments = storagePath.split('/');
  return segments[segments.length - 1] || storagePath;
}
